import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FocusEvent, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cardPos, cn, isPhrasalFront } from '@shared/lib';
import {
  groupByPos,
  isLocked,
  isRare,
  pagePhrases,
  PHRASE_PAGER,
  sectionsToOpen,
  type PhrasePager,
  type ConstructionItem,
  type SenseChoice,
  type SenseItem,
} from '../lib/senseChoice';
import { markLabels } from '../lib/usageMarks';

/**
 * Десктопный выбор значений (макет «Создание карточек v2», 2026-10-06): разделы «Активно
 * используются» и «Другие и редкие» — группы частей речи с чипами, «Фразы» и «Фразовые
 * глаголы» — строки, раскрывающие значения фразы. Подробности значения (пометы, синонимы,
 * как употреблять, пример) — по наведению.
 */

export function posName(t: TFunction, pos: string | null, short = false): string {
  const key = (pos ?? 'other').toLowerCase();
  const full = t(`cardsWorkspace.lookup.pos.${key}`, { defaultValue: pos ?? t('cardsWorkspace.lookup.pos.other') });
  return short ? t(`cardsWorkspace.lookup.posShort.${key}`, { defaultValue: full }) : full;
}

/** Есть ли у значения что показать в подробностях (у Яндекса — нет). */
export function hasDetails(x: SenseItem, yandex: boolean): boolean {
  return !yandex && (x.translations.length > 1 || !!x.usageNote || !!x.example);
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}
function ChevIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** Подробности значения: часть речи, все пометы, синонимы, как употреблять, пример.
 *  «Уже собрано / готово» не пишем (владелец 2026-10-06): каждый модуль генерирует свою
 *  карточку заново. */
export function SenseDetails({ x, className }: { x: SenseItem; className?: string }) {
  const { t } = useTranslation();
  const marks = markLabels(t, x, true);
  const synonyms = x.translations.slice(1);
  return (
    <div className={cn('flex flex-col gap-2.5 text-left text-fg', className)}>
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <span className="text-[15px] font-semibold">{x.main}</span>
        <span className="font-serif text-[14px] italic text-muted">{posName(t, x.pos)}</span>
      </div>
      {marks.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {marks.map((m) => (
            <span key={m} className="inline-flex h-[22px] items-center rounded-[6px] bg-[#34343a] px-2 text-[12px] text-[#c9c5c0]">
              {m}
            </span>
          ))}
        </div>
      )}
      {synonyms.length > 0 && (
        <div className="text-[14px] leading-[1.45] text-[#c9c5c0]">
          <span className="text-[#8a8a90]">{t('cardsWorkspace.pick.synonyms')}</span>
          {synonyms.join(', ')}
        </div>
      )}
      {x.usageNote && <div className="text-[14px] leading-[1.45] text-[#e4dfd8] [text-wrap:pretty]">{x.usageNote}</div>}
      {x.example && (
        <div className="rounded-[10px] bg-[#1e1e20] px-3 py-2.5">
          <div className="font-serif text-[15px] italic leading-[1.35]">{x.example.en}</div>
          <div className="mt-1 text-[14px] leading-[1.4] text-muted">{x.example.ru}</div>
        </div>
      )}
    </div>
  );
}

/** Мышь: подробности через паузу, чтобы не мигали при проходе курсором. */
const HOVER_MS = 280;
/** Палец (iPad, сенсорный ноутбук): долгое нажатие, как на этапе 2 телефона. */
const HOLD_MS = 420;
/** Ширина карточки подробностей и отступы от краёв окна и чипа. */
const PEEK_WIDTH = 340;
const PEEK_EDGE = 16;
const PEEK_GAP = 6;
/** Tab к чипу за краем прокрутки: браузер сам прокручивает список к чипу, а событие scroll приходит
 *  кадром позже. Прокрутку в эти миллисекунды после показа по фокусу считаем частью фокуса. */
const FOCUS_SETTLE_MS = 400;

/** Чьи подробности открыты: наведением (карточка у чипа) или долгим нажатием (по центру). */
type Peek = { id: string; mode: 'hover' | 'hold' } | null;

/**
 * Подробности у чипа — порталом в `body` (ТЗ главной § 6): край прокрутки листа или раздела
 * их не обрезает. Под чипом, а если до низа окна не помещаются — над ним; по горизонтали —
 * в пределах `[data-chip-bounds]` и окна. Прокрутка и смена размера окна уводят чип из-под
 * карточки — тогда она закрывается. Исключение — прокрутка, которую начал сам фокус (до
 * `settleUntil`): карточка встаёт к чипу на новое место. Клики не ловит: наведение остаётся на чипе.
 */
function PeekCard({
  x,
  anchorEl,
  settleUntil,
  onDismiss,
}: {
  x: SenseItem;
  /** Обёртка чипа: позицию меряем по ней при показе и после прокрутки от фокуса. */
  anchorEl: HTMLElement;
  /** До этого момента (`performance.now()`) прокрутка не закрывает карточку, а переставляет её. */
  settleUntil: number;
  onDismiss: () => void;
}) {
  const card = useRef<HTMLDivElement>(null);
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  const width = Math.min(PEEK_WIDTH, window.innerWidth - PEEK_EDGE * 2);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const place = useCallback(() => {
    const anchor = anchorEl.getBoundingClientRect();
    // Край — по контейнеру групп (он срезает по горизонтали) и по окну.
    const bounds = anchorEl.closest('[data-chip-bounds]')?.getBoundingClientRect();
    const height = card.current?.offsetHeight ?? 0;
    const right = Math.min(bounds?.right ?? Infinity, window.innerWidth - PEEK_EDGE);
    const left = Math.max(Math.max(bounds?.left ?? 0, PEEK_EDGE), Math.min(anchor.left, right - width));
    const below = anchor.bottom + PEEK_GAP;
    const fitsBelow = below + height <= window.innerHeight - PEEK_EDGE;
    setPos({ top: fitsBelow ? below : Math.max(PEEK_EDGE, anchor.top - PEEK_GAP - height), left });
  }, [anchorEl, width]);

  useLayoutEffect(place, [place]);

  useEffect(() => {
    const onScroll = () => {
      if (performance.now() < settleUntil) place();
      else dismiss.current();
    };
    const onResize = () => dismiss.current();
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onResize);
    };
  }, [place, settleUntil]);

  return createPortal(
    <div
      ref={card}
      role="tooltip"
      className="pointer-events-none fixed z-[60] rounded-2xl bg-[#26262b] px-4 pb-4 pt-3.5 shadow-[0_0_0_1px_#34343a,0_24px_48px_-12px_rgba(0,0,0,0.75)]"
      // Первый кадр — невидимый замер высоты, позиция ставится до отрисовки (useLayoutEffect).
      style={pos ? { top: pos.top, left: pos.left, width } : { top: 0, left: 0, width, visibility: 'hidden' }}
    >
      <SenseDetails x={x} />
    </div>,
    document.body,
  );
}

/** Подробности по долгому нажатию: по центру экрана с подложкой, закрываются отпусканием,
 *  крестиком и касанием подложки — как на этапе 2 телефона. */
function HoldPeekLayer({ x, onClose }: { x: SenseItem; onClose: () => void }) {
  const { t } = useTranslation();
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex select-none items-center justify-center bg-black/60 px-6 [-webkit-touch-callout:none]"
      onClick={onClose}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        className="relative w-full max-w-[340px] rounded-2xl bg-[#2a2a2f] px-4 pb-4 pt-3.5 shadow-[0_0_0_1px_#3a3a40,0_24px_48px_-12px_rgba(0,0,0,0.85)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('cardsWorkspace.pick.peekClose')}
          className="absolute right-1 top-1 grid h-11 w-11 place-items-center rounded-full text-muted active:bg-[#34343a]"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div className="pr-9">
          <SenseDetails x={x} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Цвета чипа по состоянию (макет): выбран, учишь, создаётся, обычный. */
function chipTone(x: SenseItem, on: boolean) {
  if (on) return { bg: '#f36a1c', line: '#f36a1c', fg: '#141416', mfg: 'rgba(20,20,22,0.62)' };
  if (x.learned) return { bg: 'rgba(61,214,140,0.1)', line: 'rgba(61,214,140,0.35)', fg: '#3dd68c', mfg: 'rgba(61,214,140,0.7)' };
  if (x.generating) return { bg: 'rgba(243,106,28,0.1)', line: 'rgba(243,106,28,0.3)', fg: '#ff9a5c', mfg: 'rgba(255,154,92,0.7)' };
  return { bg: 'transparent', line: '#34343a', fg: '#f5f1ec', mfg: '#8a8a90' };
}

function SenseChip({
  x,
  choice,
  yandex,
  peek,
  onPeek,
  readOnly,
}: {
  x: SenseItem;
  choice: SenseChoice;
  yandex: boolean;
  peek: Peek;
  onPeek: (next: Peek) => void;
  /** Редкое значение: не отмечается (2026-10-07), подробности — по наведению. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const locked = isLocked(x);
  const ro = !!readOnly && !locked;
  const on = !ro && !locked && choice.isSelected(x.id);
  const dim = !ro && !on && !locked && choice.atLimit;
  const tone = ro ? { bg: 'transparent', line: '#2a2a30', fg: '#a9a9ae', mfg: '#6a6a70' } : chipTone(x, on);
  const marks = yandex ? [] : markLabels(t, x);
  const info = hasDetails(x, yandex);
  const tag = x.learned
    ? t('cardsWorkspace.pick.tagLearned')
    : x.generating
      ? t('cardsWorkspace.pick.tagGenerating')
      : '';
  let title = on ? t('cardsWorkspace.pick.titleOn') : t('cardsWorkspace.pick.titleOff');
  if (x.learned) title = t('cardsWorkspace.pick.titleLearned');
  else if (x.generating) title = t('cardsWorkspace.pick.titleGenerating');
  else if (ro) title = t('cardsWorkspace.pick.titleRare');
  else if (dim) title = t('cardsWorkspace.pick.titleLimit', { limit: choice.limit });

  // Подробности: мышью — наведение с паузой, с клавиатуры — :focus-visible, пальцем — долгое
  // нажатие. Только `pointerType === 'mouse'`: после касания браузер шлёт поддельный
  // mouseenter, и карточка открывалась от обычного тапа.
  const wrap = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const frame = useRef<number | undefined>(undefined);
  const holdFired = useRef(false);
  const offHold = useRef<(() => void) | null>(null);
  const [anchor, setAnchor] = useState<{ el: HTMLElement; settleUntil: number } | null>(null);
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      if (frame.current !== undefined) window.cancelAnimationFrame(frame.current);
      offHold.current?.();
    },
    [],
  );
  /** `settleUntil` — до какого момента прокрутка не закрывает карточку (показ по фокусу), 0 — закрывает. */
  const show = (settleUntil = 0) => {
    const el = wrap.current;
    if (!el) return;
    setAnchor({ el, settleUntil });
    onPeek({ id: x.id, mode: 'hover' });
  };
  const cancelFrame = () => {
    if (frame.current !== undefined) window.cancelAnimationFrame(frame.current);
    frame.current = undefined;
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    cancelFrame();
    if (peek?.id === x.id && peek.mode === 'hover') onPeek(null);
  };
  const startHold = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      holdFired.current = true;
      if (navigator.vibrate) navigator.vibrate(8);
      onPeek({ id: x.id, mode: 'hold' });
      // Закрывается, когда палец отпущен. Пока открыто, прокрутку и системное меню долгого
      // нажатия гасим: иначе браузер забирает жест и подробности сразу пропадают.
      const noScroll = (e: TouchEvent) => e.preventDefault();
      const noMenu = (e: Event) => e.preventDefault();
      const off = () => {
        ['pointerup', 'touchend'].forEach((name) => window.removeEventListener(name, off, true));
        window.removeEventListener('touchmove', noScroll, true);
        window.removeEventListener('contextmenu', noMenu, true);
        offHold.current = null;
        onPeek(null);
      };
      offHold.current?.();
      ['pointerup', 'touchend'].forEach((name) => window.addEventListener(name, off, true));
      window.addEventListener('touchmove', noScroll, { capture: true, passive: false });
      window.addEventListener('contextmenu', noMenu, true);
      offHold.current = off;
    }, HOLD_MS);
  };
  const onPointerEnter = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => show(), HOVER_MS);
  };
  const onFocus = (e: FocusEvent<HTMLButtonElement>) => {
    // Только фокус с клавиатуры. Safari младше 15.4 не знает :focus-visible и бросает
    // исключение — там показываем на любой фокус, как раньше.
    let keyboard = true;
    try {
      keyboard = e.currentTarget.matches(':focus-visible');
    } catch {
      // старый движок
    }
    if (!keyboard) return;
    // Tab к чипу, которого не видно в прокручиваемом разделе (лист главной, WordSpotlight): браузер
    // прокручивает список к чипу, и scroll приходит в следующем кадре — карточка, показанная сразу,
    // встала бы по старому месту и тут же закрылась. Меряем после прокрутки, а прокрутку первых
    // FOCUS_SETTLE_MS считаем частью фокуса: она переставляет карточку, а не закрывает.
    cancelFrame();
    frame.current = window.requestAnimationFrame(() => {
      frame.current = undefined;
      show(performance.now() + FOCUS_SETTLE_MS);
    });
  };
  const closeHold = () => (offHold.current ? offHold.current() : onPeek(null));

  return (
    <span
      ref={wrap}
      className="relative inline-flex max-w-full"
      onPointerEnter={info ? onPointerEnter : undefined}
      onPointerLeave={info ? (e) => e.pointerType === 'mouse' && hide() : undefined}
    >
      <button
        type="button"
        onClick={() => {
          // Click после долгого нажатия чип не переключает.
          if (holdFired.current) {
            holdFired.current = false;
            return;
          }
          if (!locked && !ro) choice.toggle(x.id);
        }}
        onPointerDown={(e) => {
          // Сброс на любом нажатии: если после удержания click не пришёл, следующий не потеряется.
          holdFired.current = false;
          if (info && e.pointerType !== 'mouse') startHold();
        }}
        onPointerUp={(e) => e.pointerType !== 'mouse' && window.clearTimeout(timer.current)}
        onPointerCancel={() => window.clearTimeout(timer.current)}
        onContextMenu={(e) => e.preventDefault()}
        onFocus={info ? onFocus : undefined}
        onBlur={info ? hide : undefined}
        aria-pressed={ro ? undefined : on}
        aria-disabled={ro || locked || dim || undefined}
        title={info ? undefined : title}
        aria-label={info ? `${x.main} — ${title}` : undefined}
        style={{ background: tone.bg, boxShadow: `inset 0 0 0 1px ${tone.line}`, color: tone.fg }}
        className={cn(
          'inline-flex min-h-[32px] max-w-full select-none items-center gap-1.5 rounded-2xl px-3 py-[5px] text-left transition-[background,box-shadow,opacity] duration-150 [-webkit-touch-callout:none] hover:brightness-[1.15]',
          dim && 'opacity-[0.38]',
          locked || ro ? 'cursor-default' : dim ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        {(on || x.learned) && <CheckIcon className="h-3 w-3 shrink-0" />}
        {x.generating && <span className="h-[7px] w-[7px] shrink-0 animate-pulse rounded-full bg-current" />}
        <span className="min-w-0 text-[14px] font-medium leading-[1.25]">{x.main}</span>
        {marks.length > 0 && (
          <span className="shrink-0 whitespace-nowrap text-[12px] font-medium leading-[1.25]" style={{ color: tone.mfg }}>
            {marks.join(' · ')}
          </span>
        )}
        {tag && (
          <span
            className="shrink-0 text-[9.5px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: on ? 'rgba(20,20,22,0.62)' : x.learned ? '#3dd68c' : x.generating ? '#ff9a5c' : '#9a9a9a' }}
          >
            {tag}
          </span>
        )}
      </button>
      {info && peek?.id === x.id && peek.mode === 'hover' && anchor && (
        <PeekCard x={x} anchorEl={anchor.el} settleUntil={anchor.settleUntil} onDismiss={hide} />
      )}
      {info && peek?.id === x.id && peek.mode === 'hold' && <HoldPeekLayer x={x} onClose={closeHold} />}
    </span>
  );
}

function SectionHeader({
  title,
  count,
  selected,
  preview,
  collapsible,
  open,
  onToggle,
}: {
  title: string;
  count: number;
  selected: number;
  preview?: string;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={collapsible ? onToggle : undefined}
      aria-expanded={collapsible ? open : undefined}
      className={cn(
        'flex min-w-0 flex-1 items-baseline gap-2.5 bg-transparent p-0 text-left text-fg',
        collapsible ? 'cursor-pointer' : 'cursor-default',
      )}
    >
      <span className="shrink-0 font-serif text-[17px] font-medium">{title}</span>
      <span className="shrink-0 text-[14px] text-[#8a8a90]">{count}</span>
      {selected > 0 && (
        <span className="shrink-0 text-[14px] font-semibold text-[#ff9a5c]">
          {t('cardsWorkspace.pick.selected', { n: selected })}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-[14px] text-[#6a6a70]">{collapsible && !open ? preview : ''}</span>
      {collapsible && (
        <ChevIcon className={cn('h-4 w-4 shrink-0 self-center text-muted transition-transform duration-200', open && 'rotate-180')} />
      )}
    </button>
  );
}

/** Группы частей речи в одну строку через тонкие разделители (первый срезается overflow). */
function SenseGroups({
  items,
  choice,
  yandex,
  peek,
  onPeek,
  readOnly,
}: {
  items: SenseItem[];
  choice: SenseChoice;
  yandex: boolean;
  peek: Peek;
  onPeek: (next: Peek) => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div data-chip-bounds className="[overflow-x:clip]">
      <div className="-ml-[15px] flex flex-wrap items-center gap-y-2.5">
        {groupByPos(items).map((g, i) => (
          <div
            key={`${g.pos ?? 'x'}-${i}`}
            className="box-border flex max-w-full flex-wrap items-center gap-1.5 border-l border-[#3a3a40] px-3.5"
          >
            <span className="mr-[3px] shrink-0 font-serif text-[15px] font-medium italic text-[#e4dfd8]">
              {posName(t, cardPos(g.pos, isPhrasalFront(choice.view?.word)), true)}
            </span>
            {g.items.map((x) => (
              <SenseChip
                key={x.id}
                x={x}
                choice={choice}
                yandex={yandex}
                peek={peek}
                onPeek={onPeek}
                readOnly={readOnly}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SmallButton({ children, onClick, dashed }: { children: React.ReactNode; onClick: () => void; dashed?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-pill px-3 text-[14px] font-medium',
        dashed
          ? 'border border-dashed border-[#3a3a40] px-[13px] text-[#c9c5c0] hover:border-[#6a6a70] hover:text-fg'
          : 'text-muted hover:bg-[#24242a] hover:text-fg',
      )}
    >
      {children}
    </button>
  );
}


/** Строка фразы: одно значение — выбирается сразу, несколько — раскрываются чипами. */
function ConstructionRow({
  c,
  choice,
  open,
  onOpen,
  peek,
  onPeek,
}: {
  c: ConstructionItem;
  choice: SenseChoice;
  open: boolean;
  onOpen: () => void;
  peek: Peek;
  onPeek: (next: Peek) => void;
}) {
  const { t } = useTranslation();
  const single = c.senses.length === 1;
  const x0 = c.senses[0];
  const selN = c.senses.filter((x) => choice.isSelected(x.id)).length;
  const locked = single && isLocked(x0);
  const on = single && selN === 1;
  const dim = single && !on && !locked && choice.atLimit;
  const marks = markLabels(t, {
    registers: single ? [...c.registers, ...x0.registers] : c.registers,
    region: single ? [...c.region, ...x0.region] : c.region,
    grammar: single ? x0.grammar : [],
  });
  const extra = c.senses.length - 1;
  const isOpen = !single && open;
  // Фраза с одним значением — два перевода: «при этом» без контекста расплывчато, «при этом,
  // попутно» понятнее (решение владельца 2026-10-06). У нескольких значений второй перевод
  // выглядел бы как другое значение — там только главный.
  const label = single ? x0.translations.slice(0, 2).join(', ') : x0.main;
  const tag = locked ? (x0.learned ? t('cardsWorkspace.pick.tagLearned') : t('cardsWorkspace.pick.tagGenerating')) : '';
  const title = single
    ? locked
      ? x0.learned
        ? t('cardsWorkspace.pick.rowLearned')
        : t('cardsWorkspace.pick.titleGenerating')
      : dim
        ? t('cardsWorkspace.pick.titleLimit', { limit: choice.limit })
        : on
          ? t('cardsWorkspace.pick.titleOn')
          : t('cardsWorkspace.pick.rowSelect')
    : isOpen
      ? t('cardsWorkspace.pick.rowHide')
      : t('cardsWorkspace.pick.rowShow');
  return (
    <div
      className={cn('min-w-0 rounded-xl transition-[background,box-shadow] duration-150', dim && 'opacity-40')}
      style={{
        background: selN ? 'rgba(243,106,28,0.07)' : isOpen ? '#232327' : 'transparent',
        boxShadow: `inset 0 0 0 1px ${selN ? 'rgba(243,106,28,0.35)' : isOpen ? '#2e2e34' : 'transparent'}`,
      }}
    >
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={single ? () => !locked && choice.toggle(x0.id) : onOpen}
          aria-pressed={single ? on : undefined}
          aria-expanded={single ? undefined : isOpen}
          title={title}
          className={cn(
            'grid min-h-[40px] min-w-0 flex-1 grid-cols-[18px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-xl bg-transparent py-1.5 pl-2.5 pr-1 text-left text-fg',
            locked ? 'cursor-default' : dim ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          <span className="flex h-[18px] w-[18px] items-center justify-center">
            {single ? (
              <span
                className="flex h-[18px] w-[18px] items-center justify-center rounded-full transition-colors"
                style={{
                  background: on ? '#f36a1c' : x0.learned ? 'rgba(61,214,140,0.15)' : 'transparent',
                  boxShadow: `inset 0 0 0 1.5px ${on ? '#f36a1c' : x0.learned ? '#3dd68c' : x0.generating ? '#ff9a5c' : '#4a4a50'}`,
                  color: on ? '#141416' : '#3dd68c',
                }}
              >
                {(on || x0.learned) && <CheckIcon className="h-[11px] w-[11px]" />}
              </span>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="#9a9a9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cn('h-[15px] w-[15px] transition-transform duration-200', isOpen && 'rotate-90')} aria-hidden>
                <path d="M9 6l6 6-6 6" />
              </svg>
            )}
          </span>
          <span className="flex min-w-0 flex-wrap items-baseline gap-x-[9px] gap-y-px">
            <span className="font-serif text-[16px] font-medium" style={{ color: single && x0.learned ? '#3dd68c' : '#f5f1ec' }}>
              {c.phrase}
            </span>
            <span className="text-[14px] text-[#c9c5c0]">{label}</span>
            {marks.length > 0 && <span className="text-[12px] font-medium text-[#8a8a90]">{marks.join(' · ')}</span>}
            {/* «учишь»/«создаётся» — сразу за переводом, как у плиток значений, а не у края строки. */}
            {tag && (
              <span
                className="text-[9.5px] font-semibold uppercase tracking-[0.08em]"
                style={{ color: x0.learned ? '#3dd68c' : '#ff9a5c' }}
              >
                {tag}
              </span>
            )}
          </span>
          <span className="flex items-center gap-2 whitespace-nowrap">
            {!single && selN > 0 && (
              <span className="inline-flex h-5 items-center rounded-pill bg-accent px-[7px] text-[12px] font-bold text-[#141416]">
                ✓ {selN}
              </span>
            )}
            {!single && <span className="text-[12px] text-[#8a8a90]">{t('cardsWorkspace.pick.moreSenses', { count: extra })}</span>}
          </span>
        </button>
      </div>
      {isOpen && (
        <div className="flex flex-wrap gap-1.5 pb-3 pl-[38px] pr-3">
          {c.senses.map((x) => (
            <SenseChip key={x.id} x={x} choice={choice} yandex={false} peek={peek} onPeek={onPeek} />
          ))}
        </div>
      )}
    </div>
  );
}

function ConstructionSection({
  title,
  list,
  choice,
  openRow,
  onOpenRow,
  peek,
  onPeek,
}: {
  title: string;
  list: ConstructionItem[];
  choice: SenseChoice;
  openRow: string | null;
  onOpenRow: (id: string | null) => void;
  peek: Peek;
  onPeek: (next: Peek) => void;
}) {
  const { t } = useTranslation();
  const [pager, setPager] = useState<PhrasePager>(PHRASE_PAGER);
  const keep = (c: ConstructionItem) => openRow === c.id || c.senses.some((x) => choice.isSelected(x.id));
  const page = pagePhrases(list, pager, keep);
  const selected = list.reduce((n, c) => n + c.senses.filter((x) => choice.isSelected(x.id)).length, 0);
  return (
    <div className="flex min-w-0 flex-col gap-2 bg-[#1e1e20] px-3.5 pb-3.5 pt-3">
      <div className="flex min-h-[30px] items-baseline gap-2.5 px-1.5">
        <span className="font-serif text-[17px] font-medium leading-[30px]">{title}</span>
        <span className="text-[14px] text-[#8a8a90]">{list.length}</span>
        {selected > 0 && (
          <span className="text-[14px] font-semibold text-[#ff9a5c]">{t('cardsWorkspace.pick.selected', { n: selected })}</span>
        )}
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,330px),1fr))] items-start gap-x-2 gap-y-0.5">
        {page.visible.map((c) => (
          <ConstructionRow
            key={c.id}
            c={c}
            choice={choice}
            open={openRow === c.id}
            onOpen={() => onOpenRow(openRow === c.id ? null : c.id)}
            peek={peek}
            onPeek={onPeek}
          />
        ))}
      </div>
      {(page.more > 0 || page.canCollapse) && (
        <div className="flex flex-wrap gap-1.5 px-0.5">
          {page.more > 0 && (
            <SmallButton dashed onClick={() => setPager(page.expand)}>
              {t('cardsWorkspace.pick.more', { n: page.more })}
              <ChevIcon className="h-3 w-3" />
            </SmallButton>
          )}
          {page.canCollapse && (
            <SmallButton onClick={() => setPager(PHRASE_PAGER)}>{t('cardsWorkspace.pick.less')}</SmallButton>
          )}
        </div>
      )}
    </div>
  );
}

/** Все разделы найденного слова (десктоп). */
export function SenseSections({ choice }: { choice: SenseChoice }) {
  const { t } = useTranslation();
  const view = choice.view!;
  const yandex = view.source === 'yandex';
  const [peek, setPeek] = useState<Peek>(null);
  // «Ввести снова» отметил значение в свёрнутых «Других» — раздел открыт при показе ответа: платное нажатие
  // показывает, за что платят (ТЗ главной § 23.6). Решается один раз на слово и применение отметок, дальше
  // раздел в руках ученика; без отметок — свёрнут, как всегда.
  const otherOpenAtStart = () => sectionsToOpen(view, choice.initialIds, choice.isSelected).other;
  const [otherOpen, setOtherOpen] = useState(otherOpenAtStart);
  const [otherDecidedFor, setOtherDecidedFor] = useState({ word: view.word, ids: choice.initialIds });
  if (otherDecidedFor.word !== view.word || otherDecidedFor.ids !== choice.initialIds) {
    setOtherDecidedFor({ word: view.word, ids: choice.initialIds });
    setOtherOpen(otherOpenAtStart());
  }
  const [rareOpen, setRareOpen] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(null);
  // Новое слово — разделы в исходное состояние («Другие» — выше, по отметкам «Ввести снова»).
  useEffect(() => {
    setRareOpen(false);
    setOpenRow(null);
    setPeek(null);
  }, [view.word]);

  const count = (list: SenseItem[]) => list.filter((x) => choice.isSelected(x.id)).length;
  // «Другие» и «Редкие» — два раздела, оба свёрнуты (решение владельца 2026-10-06): у
  // «Других» в свёрнутом виде превью значений, у «Редких» — только число.
  const otherCommon = view.other.filter((x) => !isRare(x));
  const otherRare = view.other.filter(isRare);
  const constructions: [string, ConstructionItem[]][] = (
    [
      [t('cardsWorkspace.pick.phrases'), view.phrases],
      [t('cardsWorkspace.pick.phrasalVerbs'), view.phrasalVerbs],
    ] as [string, ConstructionItem[]][]
  ).filter(([, list]) => list.length > 0);
  // Основных нет — «Другие» открыты сразу; кроме редких ничего нет — открыты «Редкие».
  const otherFoldable = view.active.length > 0;
  const otherShown = !otherFoldable || otherOpen;
  const rareFoldable = view.active.length > 0 || otherCommon.length > 0 || constructions.length > 0;
  const rareShown = !rareFoldable || rareOpen;

  return (
    <>
      {!view.active.length && constructions.length > 0 && (
        <div className="mx-3.5 mb-3.5 flex items-center gap-2.5 rounded-[14px] bg-[#24242a] px-3.5 py-2.5 text-[14px] text-[#e4dfd8]">
          <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-[7px] bg-[#34343a] font-serif text-[14px] font-semibold italic text-[#c9c5c0]">
            i
          </span>
          {view.other.length ? t('cardsWorkspace.pick.noActiveRare') : t('cardsWorkspace.pick.noActiveNone')}
        </div>
      )}
      {(view.active.length > 0 || otherCommon.length > 0) && (
        <div className="border-t border-line">
          {view.active.length > 0 && (
            <div className="flex flex-col gap-2.5 px-5 pb-3.5 pt-3">
              <div className="flex min-h-[30px] items-center gap-2.5">
                <SectionHeader title={t('cardsWorkspace.pick.active')} count={view.active.length} selected={count(view.active)} />
              </div>
              <SenseGroups items={view.active} choice={choice} yandex={yandex} peek={peek} onPeek={setPeek} />
            </div>
          )}
          {otherCommon.length > 0 && (
            <div className={cn('flex flex-col gap-2.5 px-5 pb-3.5 pt-3', view.active.length > 0 && 'border-t border-line')}>
              <div className="flex min-h-[30px] items-center gap-2.5">
                <SectionHeader
                  title={t('cardsWorkspace.pick.other')}
                  count={otherCommon.length}
                  selected={count(otherCommon)}
                  collapsible={otherFoldable}
                  open={otherShown}
                  onToggle={() => setOtherOpen((o) => !o)}
                  preview={otherCommon.slice(0, 6).map((x) => x.main).join(', ') + (otherCommon.length > 6 ? '…' : '')}
                />
              </div>
              {otherShown && (
                <SenseGroups items={otherCommon} choice={choice} yandex={yandex} peek={peek} onPeek={setPeek} />
              )}
            </div>
          )}
        </div>
      )}
      {constructions.length > 0 && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,460px),1fr))] gap-px border-t border-line bg-line">
          {constructions.map(([title, list]) => (
            <ConstructionSection
              key={`${view.word}|${title}`}
              title={title}
              list={list}
              choice={choice}
              openRow={openRow}
              onOpenRow={setOpenRow}
              peek={peek}
              onPeek={setPeek}
            />
          ))}
        </div>
      )}
      {/* Редкие — в самом конце и только для чтения (решение владельца 2026-10-07). */}
      {otherRare.length + view.rarePhrases.length > 0 && (
        <div className="flex flex-col gap-2.5 border-t border-line px-5 pb-3.5 pt-3">
          <div className="flex min-h-[30px] items-center gap-2.5">
            <SectionHeader
              title={t('cardsWorkspace.pick.rareSection')}
              count={otherRare.length + view.rarePhrases.length}
              selected={0}
              collapsible={rareFoldable}
              open={rareShown}
              onToggle={() => setRareOpen((o) => !o)}
            />
          </div>
          {rareShown && (
            <>
              <p className="text-[14px] leading-[1.4] text-[#8a8a90]">{t('cardsWorkspace.pick.rareReadOnly')}</p>
              {otherRare.length > 0 && (
                <SenseGroups items={otherRare} choice={choice} yandex={yandex} peek={peek} onPeek={setPeek} readOnly />
              )}
              {view.rarePhrases.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-muted">
                      {t('cardsWorkspace.pick.rarePhrases')}
                    </span>
                    <span className="text-[12px] text-[#6a6a70]">{view.rarePhrases.length}</span>
                  </div>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))] gap-x-4 gap-y-1">
                    {view.rarePhrases.map((c) => {
                      const learned = c.senses.some((x) => x.learned);
                      const shown = c.senses.slice(0, 3).map((x) => x.main);
                      return (
                        <div key={c.id} className="min-w-0 py-1 text-[14px] leading-[1.4] text-[#8a8a90]">
                          <span className="font-serif text-[15px] font-medium" style={{ color: learned ? '#3dd68c' : '#c9c5c0' }}>
                            {c.phrase}
                          </span>{' '}
                          — {shown.join(', ')}
                          {c.senses.length > shown.length && ` +${c.senses.length - shown.length}`}
                          {learned && (
                            <span className="ml-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-[#3dd68c]">
                              {t('cardsWorkspace.pick.tagLearned')}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
